import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useLocation } from "react-router-dom";
import { CreditCard, Lock } from "lucide-react";
import { useAuth } from "@/app/AuthContext";
import { estadoAssinatura } from "@/features/billing/api/assinatura";

const COBRANCA = "/configuracoes/cobranca";

/**
 * Bloqueio por assinatura vencida ou suspensão administrativa.
 *
 * Três escolhas deliberadas: enquanto a consulta não responde o app segue
 * aberto (falha de rede não pode virar bloqueio), a tela de cobrança nunca é
 * bloqueada por assinatura, senão o cliente ficaria preso sem caminho para
 * pagar, e a suspensão bloqueia até ela, porque pagar não desfaz o que a
 * plataforma parou.
 */
export function GuardaAssinatura() {
  const { pathname } = useLocation();
  const { estado: sessao } = useAuth();
  const papel = sessao.situacao === "pronto" ? sessao.membro.papel : "atendente";

  const { data: estado } = useQuery({
    queryKey: ["assinatura"],
    queryFn: estadoAssinatura,
    staleTime: 5 * 60_000,
  });

  if (estado?.suspensa) {
    return (
      <Parede
        icone={Lock}
        titulo="O acesso foi suspenso"
        texto={
          estado.suspensaMotivo
            ? `${estado.suspensaMotivo} Fale com o suporte do Samev para resolver.`
            : "Fale com o suporte do Samev para resolver."
        }
      />
    );
  }

  if (!estado || estado.acesso.nivel !== "bloqueado" || pathname.startsWith(COBRANCA)) {
    return <Outlet />;
  }

  const gestao = papel === "dono" || papel === "admin";

  return (
    <Parede
      icone={CreditCard}
      titulo="O acesso ao Samev está pausado"
      texto={`${estado.acesso.motivo} ${
        gestao
          ? "Assim que a assinatura for regularizada, tudo volta exatamente como estava: conversas, contatos e histórico continuam aqui."
          : "Avise quem cuida da conta da empresa. Nada foi perdido: conversas, contatos e histórico continuam aqui."
      }`}
      acao={
        gestao ? (
          <Link
            to={COBRANCA}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CreditCard className="size-4" aria-hidden />
            Ver a assinatura
          </Link>
        ) : null
      }
    />
  );
}

function Parede({
  icone: Icone,
  titulo,
  texto,
  acao,
}: {
  icone: typeof CreditCard;
  titulo: string;
  texto: string;
  acao?: React.ReactNode;
}) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-md space-y-4 text-center">
        <Icone className="mx-auto size-8 text-muted-foreground" aria-hidden />

        <div className="space-y-1.5">
          <h1 className="text-base font-semibold">{titulo}</h1>
          <p className="text-xs leading-relaxed text-muted-foreground">{texto}</p>
        </div>

        {acao}
      </div>
    </div>
  );
}
